import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer,
  FiltersSubmit
} from 'components/Filters'
import { Tag } from 'components/v2/Tag'
import { IKreditsFilters, IKreditsFiltersProps } from 'features/Kredits/views/Index/Filters/types'
import { Input } from 'components/Input'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { GTBusinessSiteSuggestion } from 'gql/types.generated'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { mapFiltersToSearchQueryString } from 'features/Kredits/utils'
import SelectKreditsCategory from 'features/Kredits/components/SelectKreditsCategory'
import { intToLetter } from 'utils/number'

const messages = defineMessages({
  userFullNamePlaceholder: {
    id: 'kredits.filters.userFullNamePlaceholder',
    defaultMessage: 'E.g: John Doe'
  },
  phonePlaceholder: {
    id: 'kredits.filters.phonePlaceholder',
    defaultMessage: 'E.g: 123456789'
  },
  emailPlaceholder: {
    id: 'kredits.filters.emailPlaceholder',
    defaultMessage: 'E.g: john.doe@gmail.com'
  },
  businessSitePlaceholder: {
    id: 'businessCustomer.site.placeholder',
    defaultMessage: 'Enter business site'
  },
  businessClientPlaceholder: {
    id: 'businessCustomer.client.placeholder',
    defaultMessage: 'Enter business client'
  }
})

const defaultValues = {
  userFullName: '',
  phone: '',
  email: '',
  site: {
    id: '',
    name: ''
  },
  client: {
    id: '',
    name: ''
  },
  category: ''
}

export const KreditsFilters = ({ filters }: IKreditsFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const onSubmit = (values: IKreditsFilters): void => {
    navigate(`/kredits?${mapFiltersToSearchQueryString(values)}`)
  }

  const initialValues: IKreditsFilters = {
    userFullName: filters.userFullName || defaultValues.userFullName,
    phone: filters.phone || defaultValues.phone,
    email: filters.email || defaultValues.email,
    site: filters.site || defaultValues.site,
    client: filters.client || defaultValues.client,
    category: filters.category ?? defaultValues.category
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey): ReactElement | null => {
                      let label, value
                      const filterValue = filters[filterKey as keyof IKreditsFilters]

                      const hasValue = !isEmpty(filterValue)
                      if (!hasValue) return null
                      switch (filterKey) {
                        case 'userFullName':
                          label = <FormattedMessage id="kredits.filters.username" defaultMessage="User name" />
                          value = filterValue
                          break
                        case 'email':
                          label = <FormattedMessage id="kredits.filters.email" defaultMessage="Mail" />
                          value = filterValue
                          break
                        case 'phone':
                          label = <FormattedMessage id="kredits.filters.phone" defaultMessage="Phone" />
                          value = filterValue
                          break
                        case 'client':
                          label = (
                            <FormattedMessage id="kredits.filters.client.label" defaultMessage="Business Client" />
                          )
                          value = (filterValue as { id: string; name: string }).name
                          break
                        case 'site':
                          label = <FormattedMessage id="kredits.filters.site.label" defaultMessage="Business Site" />
                          value = (filterValue as { id: string; name: string }).name
                          break
                        case 'category':
                          label = (
                            <FormattedMessage id="businessCustomer.filters.label.category" defaultMessage="Category" />
                          )
                          value = intToLetter(filterValue as unknown as number)
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof defaultValues)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Input
                    id="userFullName"
                    value={values.userFullName}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.userFullNamePlaceholder)}
                    label={<FormattedMessage id="kredits.filters.userFullNameLabel" defaultMessage="User name" />}
                  />
                  <Input
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    label={<FormattedMessage id="kredits.filters.emailLabel" defaultMessage="Mail" />}
                  />
                  <Input
                    id="phone"
                    value={values.phone}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.phonePlaceholder)}
                    label={<FormattedMessage id="kredits.filters.phoneLabel" defaultMessage="Phone" />}
                  />
                  <BusinessClientAutocomplete
                    id="clientId"
                    value={values.client.name}
                    placeholder={intl.formatMessage(messages.businessClientPlaceholder)}
                    onChange={(client): void => {
                      setFieldValue('client', client)
                      onSubmit({
                        ...values,
                        client
                      })
                    }}
                    label={<FormattedMessage id="businessCustomer.client.label" defaultMessage="Business Client" />}
                  />
                  <BusinessSiteAutocomplete
                    id="siteId"
                    value={values.site.name}
                    placeholder={intl.formatMessage(messages.businessSitePlaceholder)}
                    onChange={(businessSite: GTBusinessSiteSuggestion): void => {
                      setFieldValue('site', businessSite)
                      onSubmit({
                        ...values,
                        site: businessSite
                      })
                    }}
                    label={<FormattedMessage id="businessCustomer.site.label" defaultMessage="Business Site" />}
                  />
                  <SelectKreditsCategory
                    id={'category'}
                    label={
                      <FormattedMessage id="businessCustomer.customerName.label.category" defaultMessage="Category" />
                    }
                    name="category"
                    onChange={(event): void => {
                      const {
                        target: { id, value }
                      } = event

                      handleChange(event)

                      onSubmit({
                        ...values,
                        [id]: value
                      })
                    }}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
